<template>
  <div class="container m-t-lg">
    <div class="columns">
      <div class="column">
        <div class="content">
          <h3>Property review cases</h3>
          <p v-if="isAdmin">
            Here you can view, update, and manage open cases as an administrator.
          </p>
          <p v-else>
            Here you can view and respond to cases related to your submissions.
          </p>
        </div>
        <form
          @submit.prevent="triggerSearch"
        >
          <label
            for="search-control"
            class="label  has-text-weight-bold"
          >
            Search by:
          </label>
          <div class="field">
            <div class="control">
              <search
                id="search-control"
                v-model="search"
                :dropdown="searchTypes"
                :attrs="{ input: { placeholder: 'Search by...', id: 'search-control' }}"
                :doing-search="doingSearch"
                @clear="clearSearchTable"
                @search="triggerSearch"
              />
            </div>
          </div>

          <p class="label">
            Refine your search.
          </p>
          <div class="field is-grouped is-block-mobile m-b-none">
            <div class="control is-expanded">
              <dropdown
                v-model="caseType"
                :default-option="`- Case type - `"
                name="caseType"
                :options="caseTypes"
              />
            </div>
            <div class="control is-expanded">
              <dropdown
                v-model="status"
                :default-option="`- Status - `"
                name="status"
                :options="statusOptions"
              />
            </div>
            <div class="control is-expanded">
              <label for="">
                <datepicker
                  v-model="startDate"
                  :disabled-dates="disabledDates"
                  name="startDate"
                  placeholder="Start date"
                  input-class="input"
                />
              </label>
            </div>
            <div class="control is-expanded">
              <label for="">
                <datepicker
                  v-model="endDate"
                  :disabled-dates="disabledDates"
                  name="endDate"
                  placeholder="End date"
                  input-class="input"
                />
              </label>
            </div>
            <div class="control">
              <button
                :disabled="doingSearch"
                :class="{ 'is-loading': doingSearch }"
                class="button search-button"
              >
                <span>Search</span>
                <span class="icon is-small">
                  <i
                    class="fas fa-search"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
            <div class="control">
              <button
                :disabled="doingSearch"
                class="button is-light clear-button"
                title="Clear"
                @click.prevent.stop="reset"
              >
                <!-- <span>Clear</span> -->
                <span class="icon is-small">
                  <i
                    class="fas fa-eraser"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
          </div>
        </form>
        <!-- <hr> -->

        <!-- My cases -->
        <div class="m-t-lg content">
          <h3>Cases list</h3>
          <div
            v-if="submissions.length === 0"
          >
            <p
              v-show="loadingSubmissions===true"
              v-text="$wording.home.text_6"
            />
            <p
              v-show="loadingSubmissions===false"
              v-text="$wording.home.text_7"
            />
          </div>
          <v-server-table
            ref="dashboard-table"
            :columns="tableColumns"
            :options="tableOptions"
            @loaded="loaded"
          >
            <template
              slot="createdOn"
              slot-scope="props"
            >
              {{ $dayjs.utc(props.row.createdOn).local().format(tableOptions.dateFormat) }}
            </template>

            <template
              slot="caseType"
              slot-scope="props"
            >
              <p v-if="props.row.caseType === 'Date Built Mismatch'">
                Year built discrepancy
              </p>
              <p v-else>
                Notice of violation
              </p>
            </template>
            <template
              slot="status"
              slot-scope="props"
            >
              <status-tag :status="props.row.status" />
            </template>
            <template
              slot="outcome"
              slot-scope="props"
            >
              <strong v-if="props.row.resolvedValid === true">
                CONFIRMED
              </strong>
              <strong v-if="props.row.resolvedValid === false">
                RETRACTED
              </strong>
            </template>
            <template
              slot="actions"
              slot-scope="props"
            >
              <button
                class="button is-small is-secondary"
                :class="{ 'is-loading': loading[props.row.id] }"
                :disabled="loading[props.row.id]"
                @click="openCase(props.row)"
              >
                <span>View</span>
              </button>
            </template>
          </v-server-table>
        </div>
      </div>
    </div>
    <router-view />
  </div>
</template>

<script>

// import MoreInfo from '@/components/modals/MoreInfo';
import Vue from 'vue';
import Search from "@/components/common/Search";
import Datepicker from 'vuejs-datepicker';
import { options } from '@/config/tableOptions';
import { ServerTable } from 'vue-tables-2';
import Searches from '@/mixins/searches';

Vue.use(
  ServerTable,
  {},
  false,
  'bulma'
);

export default {
  name: "Cases",
  components: {
    // MoreInfo,
    Search,
    Datepicker,
  },
  mixins: [ Searches ],
  data() {
    return {
      loading: [],
      caseType: null,
      statusOptions: {
        'attention': 'Needs attention',
        'Open': 'Open',
        'Closed': 'Closed',
        'Resolved': 'Resolved',
      },
      caseTypes: {
        'NOV': 'Notice of violation',
        'Date Built Mismatch': 'Year built discrepancy',
      },
      disabledDates: {
        from: new Date(),
      },
      orderBy: 'createdOn',
      searchAction: 'cases/searchCases',
      allowedRoutes: [ 'cases' ],
      loadingSubmissions: null,
      returnFromSearchParam: 'cases',
      searchTypes: { 'opaAccountNumber': 'Address', 'caseId': 'Case Number' },

      tableOptions: options({
        requestFunction: this.requestFunction,
        responseAdapter: this.responseAdapter,
        headings: {
          id: 'Case #',
          caseType: 'Type',
          createdOn: "Date Opened",
        },
        sortable: [ 'createdOn' ],
        dateColumns: [ 'createdOn' ],
        orderBy: {
          ascending: false,
          column: 'createdOn',
        },
        filterable: [],
        resizableColumns: true,
        columnsClasses: {
          status: 'is-status nowrap has-text-centered',
          createdOn: 'nowrap has-text-centered',
          caseType: 'has-text-centered',
          actions: 'has-text-left actions p-sm',
          opaAccountNumber: 'has-text-centered',
          outcome: 'is-status nowrap',
        },
      }),
      // Your submissions table      
      tableColumns: [
        // 'id',
        'opaAccountNumber',
        'createdOn',
        'status',
        'outcome',
        'caseType',
        'actions',
      ],
    };
  },
  computed: {
    submissions() {
      return this.$store.state.cases.cases || [];
    },
  },
  beforeMount() {
    if (!this.isAdmin) {
      delete this.statusOptions['attention'];
    }
  },
  methods: {
    async openCase(c) {
      if (!this.loading[c.id]) {
        // this.loading[c.id] = true;
        this.$set(this.loading, c.id, true);
        this.$store.commit('cases/setLastQueryState', this.$route.query);
        if (await this.$store.dispatch('cases/getCase', c.id)) {
          this.$router.push({ name: 'single-case', params: { uuid: c.id }});
          this.$set(this.loading, c.id, false);
        } else {
          this.$set(this.loading, c.id, false);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  &.is-fullheight {
    height: 100%;
  }
  &.has-bottom-button {
    .card-content {
      padding-bottom: 3rem;
      .button {
        position: absolute;
        bottom: 0;
        right: 0;
      }
    }
  }
}
</style>